import React from 'react';
import { Spinner } from 'react-bootstrap';
import { ServicesContext } from '~/services';
import { MediaPreview } from '~/components/MediaPreview';
import { useRoleActions } from '~/util/useRoleActions';
import { enumValue, filenameParts } from '~/util';
import { Editable } from '~/components/Form';

interface FileListProps {
    uploads: MediaFileType[];
    disabled?: boolean;
    gapType?: null | number;
    setEditing: (x: boolean) => void;
}
function FileList({ uploads, disabled = false, gapType = null, setEditing }: FileListProps) {
    const context = React.useContext(ServicesContext);
    const rolePermission = useRoleActions(
        gapType === enumValue(['gap', 'type', 'Risk Gap'])
            ? 'allow_add_edit_attachments_risk_gap'
            : 'allow_add_edit_attachments_quality_gap'
    );
    const [editingId, setEditingId] = React.useState<null | number>(null);
    const [loading, setLoading] = React.useState(false);
    const [serverError, setServerError] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    async function removeFile(file: MediaFileType) {
        try {
            setEditing(true);
            setLoading(true);
            await context.patient.removeMedia(file.id);
        } catch (e) {
            // Empty catch
        } finally {
            setEditing(false);
            setLoading(false);
        }
    }

    async function saveFilename(id: number, name: string) {
        try {
            setEditing(true);
            setIsSubmitting(true);
            await context.patient.editFilename(id, {
                original_filename: name,
            });
            setEditingId(null);
        } catch (e) {
            setServerError(e.message);
        } finally {
            setEditing(false);
            setIsSubmitting(false);
        }
    }

    return (
        <div>
            {(loading || disabled) && (
                <div className="container text-center">
                    <Spinner animation="border" role="status" className="me-1" />
                </div>
            )}
            {!(loading || disabled) &&
                uploads.map((file) => (
                    <div className="row" key={file.id}>
                        <div className={editingId === null ? 'col-10' : 'col-12'}>
                            <MediaPreview
                                media={file}
                                gapType={gapType}
                                isEditing={editingId === file.id}
                            >
                                <Editable
                                    handleCancel={() => setEditingId(null)}
                                    handleSave={(name) => saveFilename(file.id, name)}
                                    isEditing={editingId === file.id}
                                    isSubmitting={isSubmitting}
                                    placeholder="Filename"
                                    readonlyText={filenameParts(file.original_filename)[1]}
                                    serverError={serverError}
                                    writeableText={
                                        filenameParts(file.original_filename)[0] as string
                                    }
                                />
                            </MediaPreview>
                        </div>
                        {editingId === null && rolePermission && (
                            <div className="col-2 text-end">
                                <button
                                    className="icon-button"
                                    type="button"
                                    disabled={loading}
                                    tabIndex={0}
                                    onClick={() => {
                                        setEditingId(file.id);
                                    }}
                                    aria-label="Edit"
                                >
                                    <i className="fa-solid fa-pencil" />
                                </button>
                                <button
                                    className="icon-button"
                                    type="button"
                                    disabled={loading}
                                    tabIndex={0}
                                    onClick={() => removeFile(file)}
                                    onKeyPress={() => removeFile(file)}
                                    aria-label="Remove Attachment"
                                >
                                    <i className="fa-solid fa-lg fa-times text-danger cursor-pointer" />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
        </div>
    );
}

export { FileList };
